import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { IoBriefcaseOutline, IoCalendarOutline, IoCheckmark, IoCopyOutline } from "react-icons/io5";
import { normalizeGmailDraftUrl } from "../../gmailDraftUrl";
import {
  deriveCalendarEventTitle,
  writeCrmRecord,
  getCrmLinkForItem,
  updateEmailDraft,
  sendEmailDraft,
  sendSlackMessage,
  searchCrmRecords,
} from "../../businessModeService";
import {
  applyCachedCrmWrittenFields,
  rememberCrmWrittenLink,
  resolveCrmWrittenUiState,
} from "./crmWrittenLinkCache";
import { timestampFormatter, TIMESTAMP_MINUTE_FORMAT } from "../../../core/utils/timestampFormatter";
import {
  formatImplementedAtLabel,
  slackSentAtFromResult,
} from "./aiAssistImplementedAt";
import config from "../../../core/config";
import { SlackBrandIcon } from "../connectorBrandIcons";
import {
  startPerformerAttioOAuthFlow,
  startPerformerCalendarOAuthFlow,
  startPerformerGmailOAuthFlow,
  startPerformerSlackOAuthFlow,
} from "../performerGmailOAuth";
import {
  dismissCalendarConnectNudge,
  shouldShowCalendarConnectNudge,
} from "./meetingPreferences";
import {
  renderUsageOrError,
  usageOrErrorMessage,
} from "./UsageLimitBanner";
import AIMessageExpanded from "./AIMessageExpanded";
import InlineSendConfirm from "./InlineSendConfirm";
import { buildProgressSections } from "./aiProgressSections";
import { friendlyOutcomeDetail } from "./userFacingErrors";

const COPY_FEEDBACK_MS = 1500;

// Opt-out, not opt-in: shown by default, set REACT_APP_SHOW_AI_ASSIST_COST=false to hide.
export const SHOW_AI_ASSIST_COST =
  String(process.env.REACT_APP_SHOW_AI_ASSIST_COST ?? "true").trim().toLowerCase() !== "false";

/** Per-provider label/OAuth-starter/fallback-URL for the CRM confirm panel.
 * Salesforce is deprecated for now (no new connections offered anywhere in the
 * app -- see connectSourcesCatalog.js and the server's provider priority order)
 * but its backend write-back path stays intact, so this map is Attio-only rather
 * than deleted outright. */
const CRM_PROVIDER_META = {
  attio: {
    label: "Attio",
    startOAuthFlow: startPerformerAttioOAuthFlow,
    fallbackWebUrl: "https://app.attio.com",
  },
};

const crmProviderMeta = (provider) => CRM_PROVIDER_META[provider] || CRM_PROVIDER_META.attio;

export const AI_ICONS = {
  run: "✦",
  retry: "↻",
  copied: "✓",
  copyFailed: "!",
  gmail: "✉",
  external: "↗",
  reconnect: "↗",
};

/**
 * Outline calendar glyph for AI calendar badges and actions.
 *
 * @example
 * // Input: <AICalendarIcon />
 * // Output: 13px IoCalendarOutline icon
 */
const AICalendarIcon = ({ size = 13, className = "" }) => (
  <IoCalendarOutline
    size={size}
    className={["ai-calendar-icon", className].filter(Boolean).join(" ")}
    aria-hidden="true"
  />
);

/**
 * Outline CRM glyph for AI CRM badges and actions.
 *
 * @example
 * // Input: <AICrmIcon />
 * // Output: 13px IoBriefcaseOutline icon
 */
const AICrmIcon = ({ size = 13, className = "" }) => (
  <IoBriefcaseOutline
    size={size}
    className={["ai-crm-icon", className].filter(Boolean).join(" ")}
    aria-hidden="true"
  />
);

/**
 * Standard overlapping-squares copy icon for draft action buttons.
 *
 * @example
 * // Input: <AICopyIcon />
 * // Output: 14px outline copy icon
 */
const AICopyIcon = () => <IoCopyOutline size={14} aria-hidden="true" />;

const AI_STARTING_MESSAGES = [
  "Connecting to AI…",
  "Preparing your task…",
  "Starting AI run…",
];

/**
 * Rotating status line while waiting for the first progress event.
 *
 * @example
 * // Input: messages = ["A…", "B…"], intervalMs = 2000
 * // Output: alternates "A…" / "B…" every 2s
 */
const useRotatingMessage = (messages, intervalMs = 2200) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (!Array.isArray(messages) || messages.length <= 1) return undefined;
    const id = window.setInterval(() => {
      setIndex((current) => (current + 1) % messages.length);
    }, intervalMs);
    return () => window.clearInterval(id);
  }, [messages, intervalMs]);
  return messages[index] || messages[0] || "";
};

/**
 * Inline spinner for AI loading states (buttons, progress steps, panels).
 *
 * @example
 * // Input: <AILoadingSpinner size="sm" />
 * // Output: 12×12px cyan ring, continuously rotating
 */
const AILoadingSpinner = ({ size = "sm", className = "" }) => (
  <span
    className={`ai-loading-spinner ai-loading-spinner--${size}${
      className ? ` ${className}` : ""
    }`}
    aria-hidden="true"
  />
);

/**
 * Step icon for AI progress lists — spinner while running, glyphs otherwise.
 *
 * @example
 * // Input: <AIProgressStepIcon status="running" />
 * // Output: small spinner inside the circular step marker
 */
const AIProgressStepIcon = ({ status }) => {
  if (status === "running") {
    return (
      <span
        className="ai-progress-expanded__step-icon ai-progress-expanded__step-icon--running"
        aria-hidden="true"
      >
        <AILoadingSpinner size="xs" />
      </span>
    );
  }
  const glyph = status === "error" ? "!" : "✓";
  return (
    <span
      className={`ai-progress-expanded__step-icon ai-progress-expanded__step-icon--${status}`}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
};

/**
 * AI action control: icon-only in the extension, icon + label in the webapp.
 *
 * @example
 * // Input
 * <AiActionButton iconOnly icon={<AICopyIcon />} label="Copy" className="ai-draft-expanded__copy" onClick={copy} />
 * // Output
 * // Extension: 28×26px copy icon button. Webapp: copy icon + "Copy" text button.
 */
export const AiActionButton = ({
  iconOnly = false,
  icon,
  label,
  title,
  className = "",
  disabled,
  loading,
  children,
  ...rest
}) => {
  const displayTitle = title || label;
  if (iconOnly) {
    return (
      <button
        type="button"
        className={`ai-icon-action-btn${className ? ` ${className}` : ""}${
          loading ? " ai-icon-action-btn--loading" : ""
        }`}
        title={displayTitle}
        aria-label={label}
        disabled={disabled || loading}
        {...rest}
      >
        {children ||
          (loading ? (
            <AILoadingSpinner size="sm" className="ai-icon-action-btn__spinner" />
          ) : (
            <span className="ai-icon-action-btn__glyph" aria-hidden="true">
              {icon}
            </span>
          ))}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`ai-text-action-btn${className ? ` ${className}` : ""}${
        loading ? " ai-text-action-btn--loading" : ""
      }`}
      title={displayTitle}
      disabled={disabled || loading}
      {...rest}
    >
      {children || (
        <>
          {loading || icon ? (
            <span className="ai-text-action-btn__icon" aria-hidden="true">
              {loading ? (
                <AILoadingSpinner size="sm" className="ai-text-action-btn__spinner" />
              ) : (
                icon
              )}
            </span>
          ) : null}
          <span className="ai-text-action-btn__label">{loading ? "Working…" : label}</span>
        </>
      )}
    </button>
  );
};

/**
 * Expandable AI status badge: icon-only in extension, icon + status label in webapp.
 *
 * @example
 * // Input
 * <AiBadgeButton iconOnly={false} label="Ready" expanded={false} onClick={toggle} />
 * // Output
 * // Webapp: ✦ Ready ▸
 */
const AiBadgeButton = ({
  iconOnly = false,
  className = "",
  label,
  title,
  expanded,
  onClick,
  icon = AI_ICONS.run,
  working = false,
}) => {
  const showIcon = icon != null && icon !== "";

  if (iconOnly) {
    return (
      <button
        type="button"
        className={`ai-badge ai-badge--icon-only${className ? ` ${className}` : ""}${
          expanded ? " ai-badge--open" : ""
        }`}
        onClick={onClick}
        title={title || label}
        aria-label={label}
        aria-expanded={expanded}
      >
        {working ? (
          <span className="ai-badge__working-dot" aria-hidden="true" />
        ) : showIcon ? (
          <span className="ai-badge__icon" aria-hidden="true">
            {icon}
          </span>
        ) : (
          <span className="ai-badge__status-dot" aria-hidden="true" />
        )}
      </button>
    );
  }

  return (
    <button
      type="button"
      className={`ai-badge${className ? ` ${className}` : ""}${expanded ? " ai-badge--open" : ""}${
        !showIcon && !working ? " ai-badge--no-leading-icon" : ""
      }`}
      onClick={onClick}
      title={title || label}
      aria-expanded={expanded}
    >
      {working ? (
        <span className="ai-badge__working-dot" aria-hidden="true" />
      ) : showIcon ? (
        <span aria-hidden="true">{icon}</span>
      ) : null}
      <span className="ai-badge__label">{label}</span>
      <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
    </button>
  );
};

/**
 * Copy text to the clipboard, with a legacy fallback for non-secure contexts.
 *
 * @example
 * // Input: "hello world"
 * await copyTextToClipboard("hello world");
 * // Output: true (when the copy succeeded)
 */
async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }
  try {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

/** True when saving a draft failed in a way reconnecting OAuth might fix. */
function gmailDraftErrorLooksLikeAuth(err) {
  if (!err || typeof err !== "string") return false;
  const s = err.toLowerCase();
  return (
    s.includes("401") ||
    s.includes("403") ||
    s.includes("unauthorized") ||
    s.includes("invalid_grant") ||
    s.includes("invalid_scope") ||
    s.includes("invalid credentials") ||
    s.includes("access_denied") ||
    s.includes("insufficient authentication") ||
    s.includes("token has been expired") ||
    s.includes("token expired") ||
    s.includes("revoked") ||
    s.includes("re-authenticate")
  );
}

export const performerResultHasVisibleBadge = (result) => {
  if (!result || typeof result !== "object") return false;
  if (result.type === "crm") return true;
  if (result.outcome === "parked" || result.outcome === "pass_through") return true;
  if (result.outcome === "error") return true;
  return Boolean(String(result.draft || "").trim());
};

/**
 * True when the AI Assist sidebar should render an expanded draft/CRM panel.
 *
 * @example
 * // Input: { type: "crm", crm_proposed_payload: { ... } }
 * // Output: true
 */
export const performerResultHasExpandedPanel = (result) => {
  if (!result || typeof result !== "object") return false;
  if (result.type === "crm") return true;
  return Boolean(String(result.draft || "").trim());
};

/**
 * Resolve attachment filenames from a performer result payload.
 *
 * @example
 * // Input
 * resolveAttachmentDisplayNames({
 *   attachments: [{ id: "f1", filename: "KT Guide.pdf" }, { id: "f2", filename: "Checklist.docx" }],
 * });
 * // Output
 * // ["KT Guide.pdf", "Checklist.docx"]
 */
export const resolveAttachmentDisplayNames = (result) => {
  if (!result || typeof result !== "object") return [];
  const rows = Array.isArray(result.attachments) ? result.attachments : [];
  const namesFromAttachments = rows
    .map((row) => String(row?.filename || "").trim())
    .filter(Boolean);
  if (namesFromAttachments.length > 0) {
    return [...new Set(namesFromAttachments)];
  }

  const contextFiles = Array.isArray(result.context_files) ? result.context_files : [];
  const contextFileById = {};
  contextFiles.forEach((row) => {
    const fileId = String(row?.id || "").trim();
    if (!fileId) return;
    contextFileById[fileId] = String(row?.filename || "").trim();
  });

  const attachmentIds = Array.isArray(result.attachment_ids) ? result.attachment_ids : [];
  const namesFromIds = attachmentIds
    .map((fileId) => String(fileId || "").trim())
    .filter(Boolean)
    .map((fileId) => contextFileById[fileId] || fileId);
  return [...new Set(namesFromIds)];
};

const QUALITY_PASS_SCORE = 8;

/**
 * Read draft revision count from a performer result.
 *
 * @example
 * // Input: { draft_attempts: 2 }
 * // Output: 2
 */
const getDraftAttempts = (result) => {
  if (Number.isFinite(result?.draft_attempts)) return result.draft_attempts;
  if (Number.isFinite(result?.attempts)) return result.attempts;
  return 0;
};

/**
 * Read internal drive-retry count from a performer result.
 *
 * @example
 * // Input: { internal_retry_attempts: 1 }
 * // Output: 1
 */
const getInternalRetryAttempts = (result) => Number(result?.internal_retry_attempts) || 0;

/**
 * Remove legacy attempt-visibility suffix from persisted outcome_detail strings.
 *
 * @example
 * // Input: "Draft passed quality check. Attempt visibility: draft revisions: 1; ..."
 * // Output: "Draft passed quality check."
 */
const stripLegacyAttemptVisibility = (detail) => {
  const text = String(detail || "").trim();
  const marker = "Attempt visibility:";
  const idx = text.indexOf(marker);
  if (idx === -1) return text;
  return text.slice(0, idx).trim();
};

/**
 * True when ``outcome_detail`` is quality/run metadata that belongs in the
 * collapsed Run details accordion, not the always-visible status strip.
 *
 * @example
 * // Input: "Best-effort draft produced (score 0.3/10). Context gaps: …"
 * // Output: true
 */
const isQualityRunDetail = (detail) => {
  const text = String(detail || "");
  return (
    /best-effort draft produced/i.test(text) ||
    /draft did not reach quality threshold/i.test(text) ||
    /draft passed quality check/i.test(text) ||
    /context gaps:/i.test(text)
  );
};

/**
 * Prefer a short status hint; keep score/context-gap copy in Run details.
 *
 * @example
 * // Input: { outcome_detail: "Best-effort draft produced (score 0.3/10)." }, "Type an address."
 * // Output: "Type an address."
 */
const pickStatusHint = (result, fallback) => {
  const detail = stripLegacyAttemptVisibility(result?.outcome_detail);
  if (!detail || isQualityRunDetail(detail)) return fallback;
  return detail;
};

/**
 * True when ``draft`` is the backend's placeholder for a failed generation
 * (``email_pipeline.py``'s ``state["draft"] = f"(Draft generation failed: {e})"``)
 * rather than real draft content — so the UI can style it as an error instead
 * of a normal draft body.
 *
 * @example
 * // Input: "(Draft generation failed: boom)"
 * // Output: true
 */
const isDraftGenerationFailedPlaceholder = (draft) =>
  String(draft || "").startsWith("(Draft generation failed:");

/**
 * Decide whether the run-details accordion should appear.
 *
 * @example
 * // Input: { outcome: "success", draft_attempts: 1, internal_retry_attempts: 0 }
 * // Output: false
 */
const shouldShowRunDetails = (result) => {
  if (!result || typeof result !== "object") return false;
  const draftAttempts = getDraftAttempts(result);
  const internalRetries = getInternalRetryAttempts(result);
  const score = Number(result.score);
  const missingContext = Array.isArray(result.missing_context) ? result.missing_context : [];
  if (result.outcome === "low_quality" || result.outcome === "insufficient_context") return true;
  if (missingContext.length > 0) return true;
  if (draftAttempts > 1 || internalRetries > 0) return true;
  if (Number.isFinite(score) && score < QUALITY_PASS_SCORE) return true;
  return false;
};

/**
 * Build a short summary label for the run-details accordion.
 *
 * @example
 * // Input: { draft_attempts: 3, internal_retry_attempts: 2 }
 * // Output: "3 revisions, 2 drive retries"
 */
const formatRunDetailsSummary = (result) => {
  const draftAttempts = getDraftAttempts(result) || 1;
  const internalRetries = getInternalRetryAttempts(result);
  const parts = [`${draftAttempts} revision${draftAttempts === 1 ? "" : "s"}`];
  if (internalRetries > 0) {
    parts.push(`${internalRetries} drive retr${internalRetries === 1 ? "y" : "ies"}`);
  }
  return parts.join(", ");
};

/**
 * Resolve the primary status line for an expanded AI draft result.
 *
 * @example
 * // Input: { type: "email", outcome: "success", gmail_draft_url: "https://..." }
 * // Output: { tone: "success", label: "Saved to Gmail drafts" }
 */
const resolveAiDraftStatus = (result, options = {}) => {
  const {
    isCalendarTask = false,
    isCrmTask = false,
    gmailPerformerReady = false,
    gmailAuthUrl = null,
    showReconnectForDraft = false,
    gmailDraftError = "",
  } = options;

  if (isCrmTask) {
    const providerLabel = crmProviderMeta(result?.crm_provider).label;
    if (result?.crm_written_record_id) {
      return { tone: "success", label: `Written to ${providerLabel}`, hint: "This update is now in your CRM." };
    }
    // Ambiguity also drives crm_can_write to false, but it's a different kind of
    // blocker ("too many candidates", not "not enough fields") -- check it first so
    // the status line points at the candidate picker instead of a missing-field hint.
    if (result?.outcome === "ambiguous_match") {
      const n = Array.isArray(result?.crm_candidates) ? result.crm_candidates.length : 0;
      const companyN = Array.isArray(result?.crm_company_candidates)
        ? result.crm_company_candidates.length
        : 0;
      const label =
        result?.crm_ambiguous && result?.crm_company_ambiguous
          ? `${n} possible matches, ${companyN} possible companies — pick below`
          : result?.crm_ambiguous
          ? `${n} possible matches found — pick one below`
          : `${companyN} possible companies found — pick one below`;
      return {
        tone: "warning",
        label,
        hint:
          String(result?.outcome_detail || "").trim() ||
          "Choose the correct record, or create a new one, before writing to the CRM.",
      };
    }
    // crm_can_write is false when the draft has neither a resolved existing record
    // nor enough fields to create one -- writing now would only fail at the CRM's
    // confirm-time step, so don't claim it's "ready" or offer the write button yet.
    if (result?.crm_can_write === false || result?.outcome === "insufficient_context") {
      return {
        tone: "warning",
        label: "CRM entry needs more info before it can be written",
        hint:
          String(result?.outcome_detail || "").trim() ||
          "Add a matching contact email, company domain, or deal name, then try again.",
      };
    }
    const matchFound = Boolean(result?.crm_match_found);
    const objectType = String(result?.crm_object_type || "record");
    return {
      tone: result?.crm_connected ? "success" : "warning",
      label: matchFound
        ? `CRM update ready — will update existing ${objectType}`
        : `CRM entry ready — will create a new ${objectType}`,
      hint: result?.crm_connected
        ? `Review the fields, then Write to ${providerLabel}.`
        : `Connect ${providerLabel} to write this to your CRM.`,
    };
  }

  if (isCalendarTask) {
    const slotSource = String(result?.slot_source || "").trim().toLowerCase();
    const hasPrefill = Boolean(result?.calendar_prefill_url || result?.suggested_due_date);
    let hint = "Review title, time, and notes, then Save.";
    if (slotSource === "defaults") {
      hint =
        "Suggested using your defaults. Connect Calendar for personal availability.";
    } else if (slotSource === "freebusy") {
      hint = "Open slot from your calendar — review and Save.";
    } else if (hasPrefill) {
      hint =
        "Open the link and Save in Calendar — guests are notified only when you send.";
    }
    return {
      tone: "success",
      label: hasPrefill ? "Invite ready to review" : "Calendar invite ready",
      hint,
    };
  }

  // Recipient resolution is independent of whether a Gmail draft already exists
  // (ambiguous/not-found still write a draft with a blank To). Check these
  // before the "Saved to Gmail drafts" success branch.
  if (!isCalendarTask && result?.recipient_ambiguous === true) {
    const n = Array.isArray(result?.recipient_candidates)
      ? result.recipient_candidates.length
      : 0;
    return {
      tone: "warning",
      label: "Multiple possible recipients — pick the right one",
      hint: pickStatusHint(
        result,
        n
          ? `${n} matching addresses found in recent mail. Pick one, or type a different address.`
          : "Pick the correct address, or type one, before sending."
      ),
    };
  }
  if (
    !isCalendarTask &&
    result?.recipient_ambiguous === false &&
    (result?.resolved_recipient_email == null || result?.resolved_recipient_email === "") &&
    Array.isArray(result?.recipient_candidates)
  ) {
    return {
      tone: "neutral",
      label: "No recipient found — add one before sending",
      hint: pickStatusHint(
        result,
        "No matching address in recent mail. Type the To: address to update this draft."
      ),
    };
  }

  if (result?.gmail_draft_url) {
    return { tone: "success", label: "Saved to Gmail drafts" };
  }

  if (gmailDraftError) {
    return {
      tone: "error",
      label: "Draft ready — could not save to Gmail",
      hint: gmailDraftErrorLooksLikeAuth(gmailDraftError)
        ? "Reconnect Gmail, then try again."
        : `Gmail error: ${gmailDraftError}`,
    };
  }

  if (showReconnectForDraft) {
    return {
      tone: "warning",
      label: "Draft ready — not saved to Gmail",
      hint: "Connect Gmail to save this draft automatically.",
    };
  }

  if (!gmailAuthUrl) {
    return {
      tone: "neutral",
      label: "Draft ready",
      hint: "Gmail is not linked — use Copy to save the output.",
    };
  }

  if (gmailPerformerReady) {
    return {
      tone: "warning",
      label: "Draft ready — not saved to Gmail",
      hint: "Use Try again to save this output in your mailbox.",
    };
  }

  return {
    tone: "neutral",
    label: "Draft ready",
    hint: "Not saved to Gmail — use Copy or connect Gmail to save it there.",
  };
};

/**
 * Status line for expanded AI draft panels, with optional inline action.
 *
 * @example
 * // Input: tone="success", label="Written to Attio", implementedAt="2026-08-19T11:22:00Z"
 * // Output: dot + "Written to Attio · Aug 19, 2026 4:52 PM"
 */
export const AIDraftStatusStrip = ({
  tone = "neutral",
  label,
  hint,
  action = null,
  implementedAt = "",
}) => {
  const when = formatImplementedAtLabel(implementedAt);
  return (
    <div className={`ai-draft-status-strip ai-draft-status-strip--${tone}`} aria-live="polite">
      <div className="ai-draft-status-strip__head">
        <p className="ai-draft-status-strip__line">
          <span className="ai-draft-status-strip__dot" aria-hidden="true" />
          <span className="ai-draft-status-strip__label">{label}</span>
          {when ? <span className="ai-draft-status-strip__when"> · {when}</span> : null}
        </p>
        {action}
      </div>
      {hint ? <p className="ai-draft-status-strip__hint">{hint}</p> : null}
    </div>
  );
};

/**
 * Collapsible run metadata — revisions, score, missing context.
 *
 * @example
 * // Input: result with draft_attempts=3 and score=7.5
 * // Output: collapsed "Run details (3 revisions)" accordion
 */
export const AIRunDetails = ({ result, defaultOpen = false }) => {
  if (!shouldShowRunDetails(result)) return null;

  const draftAttempts = getDraftAttempts(result) || 1;
  const internalRetries = getInternalRetryAttempts(result);
  const score = Number(result?.score);
  const missingContext = Array.isArray(result?.missing_context) ? result.missing_context : [];
  const outcomeDetail = stripLegacyAttemptVisibility(result?.outcome_detail);
  const showOutcomeDetail =
    Boolean(outcomeDetail) &&
    result?.outcome !== "success" &&
    !missingContext.some((gap) => outcomeDetail.includes(gap));

  return (
    <details className="ai-run-details" open={defaultOpen}>
      <summary className="ai-run-details__summary">
        Run details ({formatRunDetailsSummary(result)})
      </summary>
      <div className="ai-run-details__body">
        {showOutcomeDetail ? <p className="ai-run-details__line">{outcomeDetail}</p> : null}
        {Number.isFinite(score) ? (
          <p className="ai-run-details__line">Quality score: {score.toFixed(1)}/10</p>
        ) : null}
        <p className="ai-run-details__line">
          Draft revisions: {draftAttempts}
          {internalRetries > 0 ? ` · Drive retrieval retries: ${internalRetries}` : ""}
        </p>
        {missingContext.length > 0 ? (
          <p className="ai-run-details__line">
            Missing context: {missingContext.join("; ")}
          </p>
        ) : null}
      </div>
    </details>
  );
};

export const AIDraftBadge = ({ result, expanded, onToggle, iconOnly = false }) => {
  if (!result) return null;

  if (result.type === "calendar") {
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        icon={<AICalendarIcon />}
        className="ai-badge--draft ai-badge--success ai-badge--calendar"
        label="Invite"
        title="Click to review the pre-filled calendar invite"
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  if (result.type === "crm") {
    const badgeProviderLabel = crmProviderMeta(
      result.crm_proposed_payload?.provider
    ).label;
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        icon={<AICrmIcon />}
        className="ai-badge--draft ai-badge--success ai-badge--crm"
        label={result.crm_written_record_id ? `In ${badgeProviderLabel}` : "Review CRM"}
        title={
          result.crm_written_record_id
            ? `Written to ${badgeProviderLabel}${
                formatImplementedAtLabel(result.crm_written_at)
                  ? ` · ${formatImplementedAtLabel(result.crm_written_at)}`
                  : ""
              }`
            : `Click to review the proposed ${badgeProviderLabel} CRM update`
        }
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  if (result.type === "slack") {
    const slackSentAt = slackSentAtFromResult(result);
    const slackSent = Boolean(slackSentAt || result.slack_permalink);
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        icon={<SlackBrandIcon style={{ width: 14, height: 14 }} />}
        className="ai-badge--draft ai-badge--success"
        label={slackSent ? "Sent" : "Review Slack"}
        title={
          slackSent
            ? `Sent to Slack${
                formatImplementedAtLabel(slackSentAt)
                  ? ` · ${formatImplementedAtLabel(slackSentAt)}`
                  : ""
              }`
            : "Click to review the proposed Slack reply"
        }
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  if (result.type === "message") {
    const gmailSentAt = String(result.gmail_sent_at || "").trim();
    const slackSentAt = slackSentAtFromResult(result);
    const sentVia =
      gmailSentAt && slackSentAt ? "Gmail and Slack" : gmailSentAt ? "Gmail" : slackSentAt ? "Slack" : "";
    if (sentVia) {
      const sentWhen = formatImplementedAtLabel(gmailSentAt || slackSentAt);
      return (
        <AiBadgeButton
          iconOnly={iconOnly}
          className="ai-badge--draft ai-badge--success"
          label="Sent"
          title={
            sentWhen ? `Sent via ${sentVia} · ${sentWhen}` : `Sent via ${sentVia} — click to see it`
          }
          expanded={expanded}
          onClick={onToggle}
        />
      );
    }
    // Not yet sent via either channel -- fall through to the generic
    // outcome-based badge below (same "Ready"/"Review"/"AI failed" logic
    // the legacy email branch already used).
  }

  if (result.gmail_sent_at) {
    const sentWhen = formatImplementedAtLabel(result.gmail_sent_at);
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        className="ai-badge--draft ai-badge--success"
        label="Sent"
        title={sentWhen ? `Sent · ${sentWhen}` : "This email has been sent — click to see it"}
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  const isParked = result.outcome === "parked" || result.outcome === "pass_through";

  if (isParked) {
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        className="ai-badge--parked ai-badge--needs-review"
        label="Review"
        title="Couldn't handle automatically — click to see why and try again"
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  const label = {
    success: "Ready",
    insufficient_context: "Ready",
    low_quality: "Review",
    error: "AI failed",
  }[result.outcome];

  if (result.outcome === "error") {
    const errorHint =
      friendlyOutcomeDetail(stripLegacyAttemptVisibility(result.outcome_detail)) ||
      "AI run failed — click for details";
    return (
      <AiBadgeButton
        iconOnly={iconOnly}
        className="ai-badge--draft ai-badge--error"
        label={label || "AI failed"}
        title={errorHint}
        expanded={expanded}
        onClick={onToggle}
      />
    );
  }

  if (!label || !result.draft) return null;

  const badgeTitle =
    result.outcome === "low_quality"
      ? "AI draft needs review — quality score was low. Click to see it."
      : "Click to see the AI suggestion";

  return (
    <AiBadgeButton
      iconOnly={iconOnly}
      className={`ai-badge--draft ai-badge--${result.outcome}`}
      label={label}
      title={badgeTitle}
      expanded={expanded}
      onClick={onToggle}
    />
  );
};

export const AIParkExpanded = ({ result, onRetry, onDismiss, retryLoading, iconOnly = false }) => {
  if (!result) return null;
  const reason = friendlyOutcomeDetail(stripLegacyAttemptVisibility(result.outcome_detail || ""));
  const isParked = result.outcome === "parked" || result.outcome === "pass_through";
  return (
    <div className="ai-draft-expanded ai-park-expanded">
      <AIDraftStatusStrip
        tone={result.outcome === "error" ? "error" : "warning"}
        label={
          result.outcome === "error"
            ? "AI run failed"
            : isParked
              ? "Not supported yet"
              : "Needs review"
        }
        hint={reason || undefined}
      />
      <AiActionButton
        iconOnly={iconOnly}
        className="ai-park-expanded__retry"
        icon={AI_ICONS.retry}
        label="Try again"
        title="Re-run the AI on this action item"
        onClick={onRetry}
        loading={retryLoading}
      />
      {onDismiss ? (
        <button
          type="button"
          className="ai-park-expanded__dismiss"
          aria-label="Dismiss"
          title="Dismiss"
          onClick={onDismiss}
        >
          ×
        </button>
      ) : null}
    </div>
  );
};

/**
 * Expandable badge shown while the AI performer is running on one action item.
 *
 * @example
 * // Input
 * <AIWorkingBadge expanded={true} onToggle={() => {}} stepCount={3} />
 * // Output
 * // Renders: … Working… ▾
 */
export const AIWorkingBadge = ({ expanded, onToggle, stepCount = 0, iconOnly = false }) => {
  const detail = stepCount > 0 ? `${stepCount} step${stepCount === 1 ? "" : "s"} so far` : "AI is working";
  return (
    <AiBadgeButton
      iconOnly={iconOnly}
      className="ai-badge--working"
      label="Working…"
      title={`${detail} — click to ${expanded ? "hide" : "view"} progress`}
      expanded={expanded}
      onClick={onToggle}
      working
    />
  );
};

const AIProgressSection = ({ section, initiallyOpen }) => {
  const [open, setOpen] = useState(initiallyOpen);
  const prevStatusRef = useRef(section.status);

  useEffect(() => {
    if (section.status === "running") {
      setOpen(true);
    } else if (prevStatusRef.current === "running" && section.status === "done") {
      setOpen(false);
    }
    prevStatusRef.current = section.status;
  }, [section.status]);

  const meta = `${section.steps.length} update${section.steps.length === 1 ? "" : "s"}`;

  return (
    <details
      className={`ai-progress-section ai-progress-section--${section.status}`}
      open={open}
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="ai-progress-section__summary">
        <AIProgressStepIcon status={section.status} />
        <span className="ai-progress-section__title">{section.title}</span>
        <span className="ai-progress-section__latest">{section.summary}</span>
        <span className="ai-progress-section__meta">{meta}</span>
      </summary>
      <ol className="ai-progress-expanded__list">
        {(() => {
          const step = section.steps.find((s) => s?.status === "running") ||
                       section.steps[section.steps.length - 1];
          if (!step) return null;
          const status = String(step?.status || "done");
          const label = String(step?.label || "").trim() || "Working";
          const detail = String(step?.detail || "").trim();
          const whatWorked = String(step?.what_worked || "").trim();
          const whatFailed = String(step?.what_failed || "").trim();
          const zeroResultPattern = /\b0\s+(threads?|emails?|files?|documents?|results?|items?|conversations?|messages?)\b/i;
          const isZeroResult = status === "done" && (zeroResultPattern.test(detail) || zeroResultPattern.test(label));
          return (
            <li
              className={`ai-progress-expanded__step ai-progress-expanded__step--${status}${isZeroResult ? " ai-progress-expanded__step--zero" : ""}`}
            >
              <div className="ai-progress-expanded__step-head">
                <AIProgressStepIcon status={status} />
                <span className="ai-progress-expanded__step-label">{label}</span>
              </div>
              {detail ? (
                <p className="ai-progress-expanded__step-detail">{detail}</p>
              ) : null}
              {whatWorked ? (
                <p className="ai-progress-expanded__step-detail ai-progress-expanded__step-detail--good">
                  What worked: {whatWorked}
                </p>
              ) : null}
              {whatFailed ? (
                <p className="ai-progress-expanded__step-detail ai-progress-expanded__step-detail--warn">
                  What failed: {whatFailed}
                </p>
              ) : null}
            </li>
          );
        })()}
      </ol>
    </details>
  );
};

/**
 * Live progress log for an in-flight performer run.
 *
 * @example
 * // Input
 * <AIProgressExpanded
 *   aiAssistType="email"
 *   steps={[
 *     { label: "Gathered context", detail: "2 Gmail threads", status: "done" },
 *     { label: "Drafting email (attempt 1)", status: "running" },
 *   ]}
 * />
 * // Output
 * // Renders channel-aware section titles with the running step highlighted.
 */
export const AIProgressExpanded = ({ steps = [], aiAssistType = "" }) => {
  const startingMessage = useRotatingMessage(AI_STARTING_MESSAGES);
  if (!Array.isArray(steps) || steps.length === 0) {
    return (
      <div className="ai-draft-expanded ai-progress-expanded">
        <div className="ai-progress-expanded__starting" role="status" aria-live="polite">
          <AILoadingSpinner size="md" />
          <span className="ai-progress-expanded__starting-text">{startingMessage}</span>
        </div>
        <div className="ai-progress-expanded__starting-bar" aria-hidden="true" />
      </div>
    );
  }
  const sections = buildProgressSections(steps, aiAssistType);
  const runningSectionIndex = sections.findIndex((section) => section.status === "running");

  return (
    <div className="ai-draft-expanded ai-progress-expanded">
      <div className="ai-progress-expanded__sections">
        {sections.map((section, index) => (
          <AIProgressSection
            key={section.id}
            section={section}
            initiallyOpen={index === runningSectionIndex || (runningSectionIndex < 0 && index === sections.length - 1)}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * Inline button to (re-)run the AI performer on a single action item.
 * Use this in row layouts when there is no AI badge to expand yet, or as
 * an always-visible "Run again" affordance next to an existing badge.
 *
 * @example
 * // Input (item has not been processed yet)
 * <AIRunButton onClick={run} loading={false} hasResult={false} />
 * // Output
 * // Renders: ✦ Run AI
 *
 * @example
 * // Input (item already has a result)
 * <AIRunButton onClick={run} loading={false} hasResult={true} />
 * // Output
 * // Renders: ↻ Try again
 */
export const AIRunButton = ({ onClick, loading, hasResult, iconOnly = false }) => {
  const label = loading ? "Working…" : hasResult ? "Try again" : "AI Actions";
  const icon = hasResult ? AI_ICONS.retry : AI_ICONS.run;
  const title = hasResult
    ? "Run AI again on this task"
    : "Run AI Actions on this task";
  return (
    <AiActionButton
      iconOnly={iconOnly}
      className="ai-run-btn"
      icon={icon}
      label={label}
      title={title}
      onClick={onClick}
      loading={loading}
    />
  );
};

/**
 * Copy-to-clipboard button that briefly shows "Copied!" (or "Copy failed")
 * after the user clicks it, so the action isn't silent.
 *
 * @example
 * // Input
 * <CopyDraftButton text="Hello" />
 * // Output: button labelled "Copy"; after click it shows "Copied!" for ~1.5s,
 * // then returns to "Copy".
 */
export const CopyDraftButton = ({ text, iconOnly = false, showIcon = true }) => {
  const [status, setStatus] = useState("idle");
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleClick = async () => {
    const ok = await copyTextToClipboard(text);
    setStatus(ok ? "copied" : "error");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStatus("idle"), COPY_FEEDBACK_MS);
  };

  const label =
    status === "copied" ? "Copied!" : status === "error" ? "Copy failed" : "Copy";
  const icon = !showIcon
    ? null
    : status === "copied"
      ? <IoCheckmark size={14} aria-hidden="true" />
      : status === "error"
        ? AI_ICONS.copyFailed
        : <AICopyIcon />;
  const modifier =
    status === "copied"
      ? " ai-draft-expanded__copy--copied"
      : status === "error"
        ? " ai-draft-expanded__copy--error"
        : "";

  return (
    <AiActionButton
      iconOnly={showIcon ? iconOnly : false}
      className={`ai-draft-expanded__copy${modifier}`}
      icon={icon}
      label={label}
      title={label}
      onClick={handleClick}
      aria-live="polite"
    />
  );
};

/**
 * Human-readable label for a CRM payload field key.
 *
 * @example
 * // Input: "domains"
 * // Output: "Domains"
 */
const formatCrmFieldLabel = (key) => {
  const normalized = String(key || "")
    .trim()
    .replace(/_/g, " ");
  if (!normalized) return "";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/**
 * Editable-text snapshot of a proposed CRM payload's fields, for seeding the
 * per-field inputs in AICrmExpanded. Array values (e.g. email_addresses) become
 * a comma-joined string for editing; handleWrite splits them back apart.
 *
 * @example
 * buildEditableCrmFieldsText({ name: "Jane", email_addresses: ["a@b.com"] });
 * // { name: "Jane", email_addresses: "a@b.com" }
 */
const buildEditableCrmFieldsText = (fields) => {
  const entries = Object.entries(fields || {}).filter(([, v]) => v != null && v !== "");
  const text = {};
  entries.forEach(([key, value]) => {
    text[key] = Array.isArray(value) ? value.join(", ") : String(value);
  });
  return text;
};

/**
 * Click-to-edit inline text matching the sidebar's Note editor: the trigger
 * (view mode) and the input (edit mode) share the same box model, font, and
 * color, so there's no visual "this is editable" affordance beyond the
 * cursor -- editing and non-editing states are meant to look the same.
 * Cmd/Ctrl+Enter or blur commits the in-progress edit; Escape discards it.
 * Blur commits (rather than discards) so that clicking a sibling action
 * button -- which blurs this field just before its own click fires --
 * doesn't silently drop whatever the user just typed.
 *
 * `onCommit` may be async (e.g. a network save); while it's pending the field
 * is disabled, and a thrown error replaces the save hint until the next edit.
 * `onDraftChange`, if given, is called with the live in-progress text on every
 * keystroke (and reset to null once the edit is committed or discarded), so a
 * caller that needs the latest text synchronously -- e.g. to include it in a
 * "try again" request fired the instant a button is clicked -- doesn't have
 * to wait for the async commit to land in `value`.
 *
 * @example
 * <InlineEditableField value="Follow up" onCommit={(next) => save(next)} />
 */
export const InlineEditableField = ({
  value,
  onCommit,
  placeholder = "",
  multiline = false,
  ariaLabel,
  className = "",
  onDraftChange,
  renderBelowInput,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const scrollRestoreRef = useRef(null);

  // Swapping the view-mode button for the edit-mode input (or back) removes
  // whichever one currently has focus. Chromium then auto-advances focus to
  // the next tabbable element and scrolls it into view -- in Electron this
  // routinely lands on some other control near the top of the panel, making
  // the whole thing snap upward right as you click to edit (or click away).
  // Snapshotting ancestor scroll position right before the swap and
  // restoring it in a layout effect (before the browser paints) hides that.
  const captureAncestorScroll = (el) => {
    if (!el) return;
    const scrollables = [];
    for (let node = el.parentElement; node; node = node.parentElement) {
      if (node.scrollHeight > node.clientHeight) scrollables.push(node);
    }
    scrollRestoreRef.current = scrollables.map((node) => [node, node.scrollTop]);
  };

  const restoreAncestorScroll = () => {
    const captured = scrollRestoreRef.current;
    scrollRestoreRef.current = null;
    if (!captured) return;
    captured.forEach(([node, top]) => {
      if (node.scrollTop !== top) node.scrollTop = top;
    });
  };

  useEffect(() => {
    if (!isEditing) setDraft(value || "");
  }, [value, isEditing]);

  useLayoutEffect(() => {
    if (!isEditing || !inputRef.current) {
      restoreAncestorScroll();
      return undefined;
    }
    const el = inputRef.current;
    const syncHeight = () => {
      if (!multiline) return;
      // Collapsing a focused element's height (even briefly, to remeasure
      // scrollHeight) makes Electron's bundled Chromium "helpfully" re-scroll
      // ancestor containers to keep it in view -- snapping the whole panel
      // to the top on every keystroke. Regular browsers don't do this, but
      // snapshotting/restoring scroll position costs nothing either way.
      const scrollables = [];
      for (let node = el.parentElement; node; node = node.parentElement) {
        if (node.scrollHeight > node.clientHeight) scrollables.push(node);
      }
      const scrollTops = scrollables.map((node) => node.scrollTop);

      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;

      scrollables.forEach((node, i) => {
        if (node.scrollTop !== scrollTops[i]) node.scrollTop = scrollTops[i];
      });
    };
    // On this first call for a freshly-mounted multiline field, el is still at its
    // default un-expanded height (no inline style set yet) -- typically a single
    // row. If restoreAncestorScroll() (the swap-to-edit-mode snapshot, taken while
    // the trigger button -- which shows the full drafted text -- was still in the
    // DOM) ran before this, the ancestor's content is momentarily far shorter than
    // it's about to be, so the browser clamps the restored scrollTop down to
    // whatever the short height allows (typically 0) and there's nothing left to
    // re-apply once syncHeight() expands it back a moment later. Expanding first
    // means the ancestor is already tall enough for the restore to actually stick.
    syncHeight();
    restoreAncestorScroll();
    el.addEventListener("input", syncHeight);
    el.focus({ preventScroll: true });
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch (_) {
      // Best-effort cursor placement -- not every input type supports it.
    }
    return () => el.removeEventListener("input", syncHeight);
  }, [isEditing, multiline]);

  const cancelEdit = () => {
    captureAncestorScroll(inputRef.current);
    setIsEditing(false);
    setDraft(value || "");
    setError("");
    onDraftChange?.(null);
  };

  const commitEdit = async () => {
    const trimmed = String(draft || "").trim();
    if (trimmed === String(value || "").trim()) {
      cancelEdit();
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onCommit(trimmed);
      captureAncestorScroll(inputRef.current);
      setIsEditing(false);
      onDraftChange?.(null);
    } catch (err) {
      setError(usageOrErrorMessage(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEdit();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      void commitEdit();
    }
  };

  if (isEditing) {
    const InputTag = multiline ? "textarea" : "input";
    return (
      <div className="ai-inline-edit-wrap">
        <InputTag
          ref={inputRef}
          className={`ai-inline-edit-input ${className}`}
          value={draft}
          disabled={saving}
          onChange={(event) => {
            setDraft(event.target.value);
            onDraftChange?.(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => void commitEdit()}
          rows={multiline ? 1 : undefined}
          placeholder={placeholder}
          aria-label={ariaLabel}
          autoComplete="off"
        />
        {renderBelowInput ? renderBelowInput(draft) : null}
        <span className={`ai-inline-edit-hint${error ? " ai-inline-edit-hint--error" : ""}`}>
          {error || (saving ? "Saving…" : "Ctrl/Cmd+Enter to save, Escape to discard, or click away to save")}
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`ai-inline-edit-trigger${value ? "" : " ai-inline-edit-trigger--empty"} ${className}`}
      onClick={(event) => {
        captureAncestorScroll(event.currentTarget);
        setIsEditing(true);
      }}
    >
      {value || placeholder}
    </button>
  );
};

/**
 * Stable fingerprint for a CRM proposal so Try again can reset write state.
 *
 * @example
 * // Input: { object_type: "people", fields: { email_addresses: ["a@b.com"] } }
 * // Output: '{"object_type":"people",...}'
 */
export const buildCrmProposalKey = (payload = {}) => {
  const fields = payload?.fields || {};
  return JSON.stringify({
    object_type: payload?.object_type || "",
    write_kind: payload?.write_kind || "",
    match_attribute: payload?.match_attribute || "",
    fields,
    note_title: payload?.note_title || "",
    note_content: payload?.note_content || "",
    task_content: payload?.task_content || "",
    task_due: payload?.task_due || "",
  });
};

/**
 * Overlay cached CRM write ids onto a performer result for first paint.
 *
 * @example
 * withCachedCrmWrittenFields({ type: "crm", client_item_id: "wf::k::f", crm_proposed_payload: {} });
 * // adds crm_written_record_id when this browser already confirmed that proposal
 *
 * @param {object | null} result
 * @returns {object | null}
 */
export const withCachedCrmWrittenFields = (result) =>
  applyCachedCrmWrittenFields(result, buildCrmProposalKey(result?.crm_proposed_payload));

/**
 * Expanded panel for a proposed CRM write (Attio or Salesforce), with an inline
 * confirm button.
 *
 * Renders the proposed contact/company fields and note/task preview, shows
 * whether it will update an existing record or create a new one, and commits
 * the write (only) when the user clicks "Write to <Provider>".
 *
 * @example
 * // Input: result.type === "crm" with a crm_proposed_payload
 * // Output: fields + "Write to <Provider>" button that POSTs .../crm-write
 */
export const AICrmExpanded = ({ result, onRetry, retryLoading, iconOnly = false }) => {
  const itemKey = String(result?.client_item_id || result?.action_item || "").trim();
  const payload = result?.crm_proposed_payload || {};
  const provider = payload.provider || "attio";
  const providerMeta = crmProviderMeta(provider);
  const providerLabel = providerMeta.label;
  const crmProposalKey = buildCrmProposalKey(payload);
  const crmWrittenRecordId = result?.crm_written_record_id;
  const crmWrittenWebUrl = result?.crm_written_web_url;
  const initialWritten = resolveCrmWrittenUiState({
    result,
    itemKey,
    proposalKey: crmProposalKey,
  });
  const oauthPollStopRef = useRef(null);
  const crmLinkRequestVersionRef = useRef(0);
  const [writing, setWriting] = useState(false);
  const [writeError, setWriteError] = useState("");
  const [connectError, setConnectError] = useState("");
  const [attioConnectLoading, setAttioConnectLoading] = useState(false);
  const [crmConnected, setCrmConnected] = useState(Boolean(result?.crm_connected));
  const [writtenRecordId, setWrittenRecordId] = useState(initialWritten.recordId);
  const [writtenWebUrl, setWrittenWebUrl] = useState(initialWritten.webUrl);
  const [writtenAt, setWrittenAt] = useState(initialWritten.writtenAt);
  const [writtenWasCreated, setWrittenWasCreated] = useState(initialWritten.wasCreated);
  const [pickedRecordId, setPickedRecordId] = useState(null);
  const [pickedCompanyRecordId, setPickedCompanyRecordId] = useState(null);
  const [editedFields, setEditedFields] = useState(() => buildEditableCrmFieldsText(payload.fields));
  const [editedNoteTitle, setEditedNoteTitle] = useState(String(payload.note_title || ""));
  const [editedNoteContent, setEditedNoteContent] = useState(String(payload.note_content || ""));
  const [editedTaskContent, setEditedTaskContent] = useState(String(payload.task_content || ""));
  const [editedTaskDue, setEditedTaskDue] = useState(String(payload.task_due || ""));
  const [selectedExistingRecordId, setSelectedExistingRecordId] = useState(
    payload.existing_record_id || null
  );
  const [isNameEditing, setIsNameEditing] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [nameSearchLoading, setNameSearchLoading] = useState(false);
  const nameInputRef = useRef(null);
  const nameSearchDebounceRef = useRef(null);

  useEffect(() => {
    setCrmConnected(Boolean(result?.crm_connected));
  }, [result?.crm_connected]);

  useEffect(() => {
    if (!isNameEditing || !nameInputRef.current) return;
    const el = nameInputRef.current;
    el.focus();
    const len = el.value.length;
    try {
      el.setSelectionRange(len, len);
    } catch (_) {
      // Best-effort cursor placement -- not every input type supports it.
    }
  }, [isNameEditing]);

  // Search-as-you-type suggestions for the record-name field, so picking an
  // existing person/company/deal can replace a "create new" proposal instead
  // of duplicating it. Skipped when the ambiguous-candidate radio picker is
  // already showing the same choice for this entity.
  useEffect(() => {
    const query = String(nameDraft || "").trim();
    if (nameSearchDebounceRef.current) clearTimeout(nameSearchDebounceRef.current);
    if (!isNameEditing || !query || result?.crm_ambiguous) {
      setNameSuggestions([]);
      setNameSearchLoading(false);
      return undefined;
    }
    nameSearchDebounceRef.current = setTimeout(() => {
      setNameSearchLoading(true);
      searchCrmRecords({ objectType: payload.object_type, query, provider })
        .then(({ candidates }) => setNameSuggestions(candidates))
        .catch(() => setNameSuggestions([]))
        .finally(() => setNameSearchLoading(false));
    }, 300);
    return () => clearTimeout(nameSearchDebounceRef.current);
  }, [nameDraft, isNameEditing, payload.object_type, provider, result?.crm_ambiguous]);

  useEffect(() => {
    return () => {
      oauthPollStopRef.current?.();
    };
  }, []);

  useEffect(() => {
    crmLinkRequestVersionRef.current += 1;
    const nextWritten = resolveCrmWrittenUiState({
      result,
      itemKey,
      proposalKey: crmProposalKey,
    });
    setWriting(false);
    setWriteError("");
    setConnectError("");
    setAttioConnectLoading(false);
    setWrittenRecordId(nextWritten.recordId);
    setWrittenWebUrl(nextWritten.webUrl);
    setWrittenAt(nextWritten.writtenAt);
    setWrittenWasCreated(nextWritten.wasCreated);
    setPickedRecordId(null);
    setPickedCompanyRecordId(null);
    setEditedFields(buildEditableCrmFieldsText(payload.fields));
    setEditedNoteTitle(String(payload.note_title || ""));
    setEditedNoteContent(String(payload.note_content || ""));
    setEditedTaskContent(String(payload.task_content || ""));
    setEditedTaskDue(String(payload.task_due || ""));
    setSelectedExistingRecordId(payload.existing_record_id || null);
    setIsNameEditing(false);
    setNameDraft("");
    setNameSuggestions([]);
    oauthPollStopRef.current?.();
    oauthPollStopRef.current = null;
    // crmProposalKey is a content hash of payload (see buildCrmProposalKey), so it
    // already covers payload.fields/note_title/note_content/task_content/task_due.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey, crmProposalKey, crmWrittenRecordId, crmWrittenWebUrl, result?.crm_written_at]);

  useEffect(() => {
    if (!retryLoading) return;
    crmLinkRequestVersionRef.current += 1;
    setWriting(false);
    setWriteError("");
    setWrittenRecordId(null);
    setWrittenWebUrl(null);
    setWrittenAt(null);
    setWrittenWasCreated(null);
  }, [retryLoading]);

  useEffect(() => {
    let cancelled = false;
    const requestVersion = ++crmLinkRequestVersionRef.current;
    if (!itemKey) return undefined;
    const cached = resolveCrmWrittenUiState({
      result,
      itemKey,
      proposalKey: crmProposalKey,
    });
    // Fill a missing View URL or implemented-at timestamp for a write we already
    // know about. Unwritten proposals stay on "Write to Attio" with no fetch.
    if (!cached.recordId || (cached.webUrl && cached.writtenAt)) return undefined;
    getCrmLinkForItem(itemKey)
      .then((link) => {
        if (cancelled || requestVersion !== crmLinkRequestVersionRef.current) return;
        if (!link?.crm_record_id) return;
        rememberCrmWrittenLink(itemKey, {
          proposalKey: crmProposalKey,
          recordId: link.crm_record_id,
          webUrl: link.record_url || "",
          writtenAt: link.updated_at || "",
          wasCreated: typeof link.was_created === "boolean" ? link.was_created : null,
        });
        setWrittenRecordId((current) => current || link.crm_record_id);
        if (link?.record_url) {
          setWrittenWebUrl((current) => current || link.record_url);
        }
        if (link?.updated_at) {
          setWrittenAt((current) => current || link.updated_at);
        }
        if (typeof link?.was_created === "boolean") {
          setWrittenWasCreated((current) => (current === null || current === undefined ? link.was_created : current));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // Same deps as the reset effect: crmProposalKey + written fields stand in for
    // the full `result` object so a new result identity does not re-fetch /crm-link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey, crmProposalKey, crmWrittenRecordId, crmWrittenWebUrl, result?.crm_written_at]);

  const alreadyWritten = Boolean(writtenRecordId);
  const status = resolveAiDraftStatus(
    {
      ...result,
      crm_connected: crmConnected,
      crm_written_record_id: writtenRecordId,
      crm_written_at: writtenAt,
      crm_was_created: writtenWasCreated,
      crm_provider: provider,
    },
    { isCrmTask: true }
  );
  // Mirrors the backend's readiness gate (see run_crm_pipeline's `can_write`): only
  // offer "Write to <Provider>" once we're confident the write will resolve or create
  // a parent record, instead of showing it whenever the CRM happens to be connected
  // and only discovering it can't work after the user clicks it. Ambiguity (primary
  // record and/or, for deals, the linked company) is a separate gate on top: the
  // backend already reports crm_can_write=false for it, but here we specifically
  // unlock once the user has made a pick for every ambiguous group.
  const isAmbiguous = Boolean(result?.crm_ambiguous) || Boolean(result?.crm_company_ambiguous);
  const ambiguityResolved =
    (!result?.crm_ambiguous || Boolean(pickedRecordId)) &&
    (!result?.crm_company_ambiguous || Boolean(pickedCompanyRecordId));
  const canWrite = isAmbiguous ? ambiguityResolved : result?.crm_can_write !== false;

  const handleConnectCrm = () => {
    if (attioConnectLoading || crmConnected) return;
    setConnectError("");
    setAttioConnectLoading(true);
    oauthPollStopRef.current?.();
    oauthPollStopRef.current = providerMeta.startOAuthFlow(
      () => {
        setAttioConnectLoading(false);
        setCrmConnected(true);
        if (onRetry && !retryLoading) {
          onRetry();
        }
      },
      (message) => {
        setAttioConnectLoading(false);
        if (message) {
          setConnectError(String(message));
        }
      }
    );
  };

  const renderCrmPrimaryAction = () => {
    if (crmConnected || alreadyWritten) return null;
    return (
      <AiActionButton
        iconOnly={iconOnly}
        className="ai-draft-expanded__reconnect-btn ai-draft-expanded__status-primary"
        icon={AI_ICONS.reconnect}
        label={attioConnectLoading ? "Connecting…" : `Connect ${providerLabel}`}
        title={`Connect ${providerLabel} in a new tab, then write this update to your CRM`}
        onClick={handleConnectCrm}
        loading={attioConnectLoading}
        disabled={attioConnectLoading}
      />
    );
  };

  const handleWrite = async () => {
    if (writing || alreadyWritten || !itemKey || !canWrite) return;
    setWriting(true);
    setWriteError("");
    try {
      const finalFields = {};
      Object.entries(payload.fields || {}).forEach(([key, originalValue]) => {
        const edited = editedFields[key];
        if (edited === undefined) return;
        const trimmed = edited.trim();
        if (!trimmed) return;
        finalFields[key] = Array.isArray(originalValue)
          ? trimmed.split(",").map((part) => part.trim()).filter(Boolean)
          : trimmed;
      });
      const resolvedPayload = {
        ...payload,
        fields: finalFields,
        note_title: editedNoteTitle.trim() || null,
        note_content: editedNoteContent.trim() || null,
        task_content: editedTaskContent.trim() || null,
        task_due: editedTaskDue.trim() || null,
        workflow_id: result?.workflow_id || "",
        existing_record_id: selectedExistingRecordId,
      };
      if (result?.crm_ambiguous) {
        resolvedPayload.existing_record_id = pickedRecordId === "__create_new__" ? null : pickedRecordId;
      }
      if (
        result?.crm_company_ambiguous &&
        pickedCompanyRecordId &&
        pickedCompanyRecordId !== "__no_link__"
      ) {
        resolvedPayload.fields = { ...(resolvedPayload.fields || {}), associated_company: pickedCompanyRecordId };
      }
      const written = await writeCrmRecord(itemKey, resolvedPayload);
      const nextRecordId = written?.crm_record_id || "written";
      const nextWebUrl = written?.crm_web_url || "";
      const nextWrittenAt = written?.crm_written_at || "";
      const nextWasCreated =
        typeof written?.crm_was_created === "boolean" ? written.crm_was_created : null;
      setWrittenRecordId(nextRecordId);
      if (nextWebUrl) {
        setWrittenWebUrl(nextWebUrl);
      }
      if (nextWrittenAt) {
        setWrittenAt(nextWrittenAt);
      }
      setWrittenWasCreated(nextWasCreated);
      rememberCrmWrittenLink(itemKey, {
        proposalKey: crmProposalKey,
        recordId: nextRecordId,
        webUrl: nextWebUrl,
        writtenAt: nextWrittenAt,
        wasCreated: nextWasCreated,
      });
    } catch (err) {
      setWriteError(usageOrErrorMessage(err, `Failed to write to ${providerLabel}`));
    } finally {
      setWriting(false);
    }
  };

  const startNameEdit = () => {
    setNameDraft(editedFields.name ?? "");
    setIsNameEditing(true);
  };

  const cancelNameEdit = () => {
    setIsNameEditing(false);
    setNameDraft("");
    setNameSuggestions([]);
  };

  const commitNameEdit = () => {
    const trimmed = String(nameDraft || "").trim();
    if (trimmed && trimmed !== String(editedFields.name || "").trim()) {
      setEditedFields((prev) => ({ ...prev, name: trimmed }));
      setSelectedExistingRecordId(null);
    }
    cancelNameEdit();
  };

  const pickNameSuggestion = (candidate) => {
    setEditedFields((prev) => ({ ...prev, name: candidate.label }));
    setSelectedExistingRecordId(candidate.record_id);
    cancelNameEdit();
  };

  const handleNameKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      cancelNameEdit();
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      commitNameEdit();
    }
  };

  const fields = payload.fields || {};
  const fieldEntries = Object.entries(fields).filter(([, v]) => v != null && v !== "");
  const noteTitle = String(payload.note_title || "").trim();
  const noteContent = String(payload.note_content || "").trim();
  const taskContent = String(payload.task_content || "").trim();
  const objectLabel = `${String(payload.object_type || "people")} · ${String(payload.write_kind || "note")}`;
  const formattedWrittenAt = writtenAt ? timestampFormatter(writtenAt, TIMESTAMP_MINUTE_FORMAT) : "";
  const writtenAtLabel = formattedWrittenAt === "Invalid Date" ? "" : formattedWrittenAt;
  // The pipeline synthesizes note_title from note_content's first line when the
  // action item has no natural title (see _build_draft in crm_pipeline.py) -- for
  // a short note that first line IS the whole note, so title === content. Showing
  // both then would just repeat the same text twice, so collapse to one display
  // whenever they match; the title still gets written to Attio either way.
  const showNoteTitleRow = Boolean(noteTitle) && noteTitle !== noteContent;

  return (
    <div className="ai-draft-expanded ai-crm-expanded">
      <AIDraftStatusStrip
        tone={status.tone}
        label={status.label}
        hint={crmConnected ? null : status.hint}
        implementedAt={alreadyWritten ? writtenAt : ""}
        action={renderCrmPrimaryAction()}
      />

      {writeError
        ? renderUsageOrError(writeError, "p", "ai-run-details__line")
        : null}
      {connectError ? <p className="ai-run-details__line">{connectError}</p> : null}

      <div className="ai-draft-expanded__actions ai-draft-expanded__toolbar">
        <CopyDraftButton
          showIcon={false}
          text={
            alreadyWritten
              ? noteContent || taskContent || noteTitle
              : editedNoteContent || editedTaskContent || editedNoteTitle
          }
        />
        {alreadyWritten && writtenWebUrl ? (
          <a
            href={writtenWebUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`ai-crm-expanded__view-btn ai-draft-expanded__status-primary${
              iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
            }`}
            title={`Open this record in ${providerLabel}`}
          >
            {iconOnly ? (
              <span className="ai-icon-action-btn__glyph" aria-hidden="true">
                {AI_ICONS.external}
              </span>
            ) : (
              <span className="ai-text-action-btn__label">View in {providerLabel} ↗</span>
            )}
          </a>
        ) : alreadyWritten ? (
          <AiActionButton
            className="ai-crm-expanded__write-btn ai-draft-expanded__status-primary"
            label={`Written to ${providerLabel}`}
            title={`Saved in ${providerLabel} — direct link was not returned; open ${providerLabel} and search for this record`}
            onClick={() => window.open(providerMeta.fallbackWebUrl, "_blank", "noopener,noreferrer")}
            disabled={false}
          />
        ) : (
          <AiActionButton
            className="ai-crm-expanded__write-btn ai-draft-expanded__status-primary"
            label={writing ? "Writing…" : `Write to ${providerLabel}`}
            title={
              canWrite
                ? `Commit this update to your ${providerLabel} CRM`
                : isAmbiguous
                ? "Pick a record below (or create new) before writing"
                : "Missing info needed to find or create this CRM record — see the note below"
            }
            onClick={() => void handleWrite()}
            loading={writing}
            disabled={writing || !crmConnected || !canWrite}
          />
        )}
        {onRetry ? (
          <AiActionButton
            iconOnly={iconOnly}
            className="ai-draft-expanded__retry"
            icon={AI_ICONS.retry}
            label="Try again"
            title="Generate a fresh CRM proposal for this action item"
            onClick={onRetry}
            loading={retryLoading}
            disabled={writing}
          />
        ) : null}
      </div>
      {result?.crm_ambiguous ? (
        <div
          className="ai-crm-expanded__candidates"
          role="radiogroup"
          aria-label={`Possible ${payload.object_type} matches`}
        >
          <p className="ai-crm-expanded__candidates-label">
            Which {payload.object_type} record is this?
          </p>
          {(result.crm_candidates || []).map((candidate) => (
            <label className="ai-crm-expanded__candidate-option" key={candidate.record_id}>
              <input
                type="radio"
                name={`crm-candidate-${itemKey}`}
                checked={pickedRecordId === candidate.record_id}
                onChange={() => setPickedRecordId(candidate.record_id)}
              />
              {candidate.label}
            </label>
          ))}
          <label className="ai-crm-expanded__candidate-option">
            <input
              type="radio"
              name={`crm-candidate-${itemKey}`}
              checked={pickedRecordId === "__create_new__"}
              onChange={() => setPickedRecordId("__create_new__")}
            />
            None of these — create a new {payload.object_type} record
          </label>
        </div>
      ) : null}

      {result?.crm_company_ambiguous ? (
        <div
          className="ai-crm-expanded__candidates"
          role="radiogroup"
          aria-label="Possible parent company matches"
        >
          <p className="ai-crm-expanded__candidates-label">
            Which company is this deal's parent account?
          </p>
          {(result.crm_company_candidates || []).map((candidate) => (
            <label className="ai-crm-expanded__candidate-option" key={candidate.record_id}>
              <input
                type="radio"
                name={`crm-company-candidate-${itemKey}`}
                checked={pickedCompanyRecordId === candidate.record_id}
                onChange={() => setPickedCompanyRecordId(candidate.record_id)}
              />
              {candidate.label}
            </label>
          ))}
          <label className="ai-crm-expanded__candidate-option">
            <input
              type="radio"
              name={`crm-company-candidate-${itemKey}`}
              checked={pickedCompanyRecordId === "__no_link__"}
              onChange={() => setPickedCompanyRecordId("__no_link__")}
            />
            Don't link a company
          </label>
        </div>
      ) : null}

      {alreadyWritten ? (
        <dl className="ai-crm-expanded__meta">
          <div className="ai-crm-expanded__row">
            <dt>Object</dt>
            <dd>{objectLabel}</dd>
          </div>
          {writtenAtLabel ? (
            <div className="ai-crm-expanded__row">
              <dt>{writtenWasCreated ? "Created" : "Updated"}</dt>
              <dd>{writtenAtLabel}</dd>
            </div>
          ) : null}
          {fieldEntries.map(([key, value]) => (
            <div className="ai-crm-expanded__row" key={key}>
              <dt>{formatCrmFieldLabel(key)}</dt>
              <dd>{Array.isArray(value) ? value.join(", ") : String(value)}</dd>
            </div>
          ))}
          {showNoteTitleRow ? (
            <div className="ai-crm-expanded__row">
              <dt>Note</dt>
              <dd>{noteTitle}</dd>
            </div>
          ) : null}
          {taskContent ? (
            <div className="ai-crm-expanded__row">
              <dt>Task</dt>
              <dd>
                {taskContent}
                {payload.task_due ? ` (due ${payload.task_due})` : ""}
              </dd>
            </div>
          ) : null}
        </dl>
      ) : (
        <dl className="ai-crm-expanded__meta ai-crm-expanded__meta--editable">
          <div className="ai-crm-expanded__row">
            <dt>Object</dt>
            <dd>{objectLabel}</dd>
          </div>
          {fieldEntries.map(([key]) => {
            if (key === "name") {
              const showNameDropdown =
                isNameEditing &&
                !result?.crm_ambiguous &&
                (nameSearchLoading || nameSuggestions.length > 0);
              return (
                <div className="ai-crm-expanded__row ai-crm-expanded__row--editable" key={key}>
                  <dt>Name</dt>
                  <dd className="ai-crm-expanded__name-field">
                    {isNameEditing ? (
                      <div className="ai-inline-edit-wrap">
                        <input
                          ref={nameInputRef}
                          type="text"
                          className="ai-inline-edit-input"
                          autoComplete="off"
                          value={nameDraft}
                          onChange={(event) => setNameDraft(event.target.value)}
                          onKeyDown={handleNameKeyDown}
                          onBlur={cancelNameEdit}
                          placeholder="Name"
                          aria-label="Record name"
                        />
                        {showNameDropdown ? (
                          <ul className="ai-crm-expanded__name-suggestions" role="listbox">
                            {nameSearchLoading ? (
                              <li className="ai-crm-expanded__name-suggestion ai-crm-expanded__name-suggestion--loading">
                                Searching…
                              </li>
                            ) : (
                              nameSuggestions.map((candidate) => (
                                <li key={candidate.record_id}>
                                  <button
                                    type="button"
                                    className="ai-crm-expanded__name-suggestion"
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => pickNameSuggestion(candidate)}
                                  >
                                    {candidate.label}
                                  </button>
                                </li>
                              ))
                            )}
                          </ul>
                        ) : null}
                        <span className="ai-inline-edit-hint">Press Ctrl/Cmd+Enter to save</span>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={`ai-inline-edit-trigger${
                          editedFields.name ? "" : " ai-inline-edit-trigger--empty"
                        }`}
                        onClick={startNameEdit}
                      >
                        {editedFields.name || "Name"}
                      </button>
                    )}
                    {selectedExistingRecordId ? (
                      <p className="ai-crm-expanded__name-linked-hint">
                        Will update this existing {payload.object_type} record
                      </p>
                    ) : null}
                  </dd>
                </div>
              );
            }
            return (
              <div className="ai-crm-expanded__row ai-crm-expanded__row--editable" key={key}>
                <dt>{formatCrmFieldLabel(key)}</dt>
                <dd>
                  <InlineEditableField
                    value={editedFields[key] ?? ""}
                    onCommit={(next) => {
                      setEditedFields((prev) => ({ ...prev, [key]: next }));
                      return Promise.resolve();
                    }}
                    placeholder={formatCrmFieldLabel(key)}
                    ariaLabel={formatCrmFieldLabel(key)}
                  />
                </dd>
              </div>
            );
          })}
          {showNoteTitleRow ? (
            <div className="ai-crm-expanded__row ai-crm-expanded__row--editable">
              <dt>Note</dt>
              <dd>
                <InlineEditableField
                  value={editedNoteTitle}
                  onCommit={(next) => {
                    setEditedNoteTitle(next);
                    return Promise.resolve();
                  }}
                  placeholder="Note title"
                  ariaLabel="Note title"
                />
              </dd>
            </div>
          ) : null}
          {taskContent ? (
            <div className="ai-crm-expanded__row ai-crm-expanded__row--editable">
              <dt>Task</dt>
              <dd className="ai-crm-expanded__task-edit">
                <InlineEditableField
                  value={editedTaskContent}
                  onCommit={(next) => {
                    setEditedTaskContent(next);
                    return Promise.resolve();
                  }}
                  placeholder="Task"
                  ariaLabel="Task content"
                />
                {payload.task_due ? (
                  <InlineEditableField
                    value={editedTaskDue}
                    onCommit={(next) => {
                      setEditedTaskDue(next);
                      return Promise.resolve();
                    }}
                    placeholder="Due date"
                    ariaLabel="Task due date"
                    className="ai-crm-expanded__task-due-input"
                  />
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>
      )}

      {noteContent ? (
        alreadyWritten ? (
          <pre className="ai-crm-expanded__note-body">{noteContent}</pre>
        ) : (
          <div className="ai-crm-expanded__note-body-edit">
            <InlineEditableField
              value={editedNoteContent}
              onCommit={(next) => {
                setEditedNoteContent(next);
                return Promise.resolve();
              }}
              placeholder="Note content"
              ariaLabel="Note content"
              multiline
            />
          </div>
        )
      ) : null}

    </div>
  );
};

/**
 * Hydrate Slack "already sent" UI from a persisted performer result.
 *
 * @example
 * hydrateSlackSentState({ slack_sent_at: "2026-08-19T11:22:00Z", slack_permalink: "https://..." });
 * // { messageTs: "sent", permalink: "https://...", sentAt: "2026-08-19T11:22:00Z" }
 *
 * @param {object|null|undefined} result
 * @returns {{ messageTs: string, permalink: string, sentAt: string } | null}
 */
const hydrateSlackSentState = (result) => {
  const sentAt = slackSentAtFromResult(result);
  const permalink = String(result?.slack_permalink || "").trim();
  const messageTs = String(result?.slack_message_ts || "").trim();
  if (!sentAt && !permalink && !messageTs) return null;
  return {
    messageTs: messageTs || "sent",
    permalink,
    sentAt,
  };
};

/**
 * Expanded panel for a proposed Slack reply, with an inline confirm button.
 *
 * Renders the drafted message (editable inline) and posts it into the source
 * channel/thread (only) when the user clicks "Send to Slack" -- never
 * resolves a new recipient, always replies where the action item came from
 * (see slack_pipeline.py's module docstring on the backend).
 *
 * @example
 * // Input: result.type === "slack" with a slack_proposed_payload
 * // Output: message preview + "Send to Slack" button that POSTs .../slack-send
 */
export const AISlackExpanded = ({ result, onRetry, retryLoading, iconOnly = false }) => {
  const itemKey = String(result?.client_item_id || result?.action_item || "").trim();
  const payload = result?.slack_proposed_payload || {};
  const missingScope = result?.outcome === "missing_scope";
  const [editedMessage, setEditedMessage] = useState(
    String(payload.message || result?.draft || "")
  );
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sent, setSent] = useState(() => hydrateSlackSentState(result));
  const [reconnecting, setReconnecting] = useState(false);
  const slackOauthStopRef = useRef(null);

  useEffect(() => {
    setEditedMessage(String(payload.message || result?.draft || ""));
    setSending(false);
    setSendError("");
    setSent(hydrateSlackSentState(result));
    // payload/result identity changes (a fresh draft or a "Try again" re-run) reset
    // local editing/send state; a successful send deliberately doesn't re-trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey, result?.slack_channel_id, result?.slack_thread_ts, payload.message, result?.slack_sent_at]);

  useEffect(() => () => slackOauthStopRef.current?.(), []);

  const hasMessage = Boolean(String(editedMessage || "").trim());
  const canSend =
    !missingScope &&
    !sent &&
    Boolean(itemKey) &&
    Boolean(payload.channel_id) &&
    Boolean(payload.thread_ts) &&
    hasMessage;

  const handleSend = async () => {
    if (sending || sent || !canSend) return;
    setSending(true);
    setSendError("");
    try {
      const written = await sendSlackMessage(itemKey, {
        channel_id: payload.channel_id,
        thread_ts: payload.thread_ts,
        message: editedMessage.trim(),
      });
      setSent({
        messageTs: written?.slack_message_ts || "sent",
        permalink: written?.slack_permalink || "",
        sentAt: written?.slack_sent_at || slackSentAtFromResult(written),
      });
    } catch (err) {
      setSendError(usageOrErrorMessage(err, "Failed to send Slack reply"));
    } finally {
      setSending(false);
    }
  };

  const handleReconnectSlack = async () => {
    if (reconnecting) return;
    slackOauthStopRef.current?.();
    setReconnecting(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/slack/auth_url`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        setReconnecting(false);
        return;
      }
      slackOauthStopRef.current = startPerformerSlackOAuthFlow(
        authUrl,
        () => setReconnecting(false),
        () => setReconnecting(false)
      );
    } catch {
      setReconnecting(false);
    }
  };

  const statusMeta = missingScope
    ? { tone: "warning", label: "Reconnect Slack to send" }
    : sent
    ? { tone: "success", label: "Sent to Slack" }
    : hasMessage
    ? { tone: "neutral", label: "Reply ready — not sent" }
    : { tone: "error", label: "Could not draft a reply", hint: friendlyOutcomeDetail(result?.outcome_detail) || undefined };

  return (
    <div className="ai-draft-expanded ai-crm-expanded">
      <AIDraftStatusStrip
        tone={statusMeta.tone}
        label={statusMeta.label}
        hint={statusMeta.hint}
        implementedAt={sent ? sent.sentAt || slackSentAtFromResult(sent) : ""}
      />

      {sendError ? renderUsageOrError(sendError, "p", "ai-run-details__line") : null}

      <div className="ai-draft-expanded__actions ai-draft-expanded__toolbar">
        {hasMessage && !sent ? <CopyDraftButton showIcon={false} text={editedMessage} /> : null}

        {missingScope ? (
          <AiActionButton
            iconOnly={iconOnly}
            className="ai-draft-expanded__send-btn ai-draft-expanded__status-primary"
            icon={AI_ICONS.reconnect}
            label={reconnecting ? "Opening Slack…" : "Reconnect Slack"}
            title="Reconnect Slack to grant send permission"
            onClick={() => void handleReconnectSlack()}
            loading={reconnecting}
            disabled={reconnecting}
          />
        ) : sent ? (
          sent.permalink ? (
            <a
              href={sent.permalink}
              target="_blank"
              rel="noopener noreferrer"
              className={`ai-crm-expanded__view-btn ai-draft-expanded__status-primary${
                iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
              }`}
              title="Open this message in Slack"
            >
              {iconOnly ? (
                <span className="ai-icon-action-btn__glyph" aria-hidden="true">
                  {AI_ICONS.external}
                </span>
              ) : (
                <span className="ai-text-action-btn__label">View in Slack ↗</span>
              )}
            </a>
          ) : (
            <AiActionButton
              iconOnly={iconOnly}
              className="ai-draft-expanded__send-btn ai-draft-expanded__send-btn--sent"
              icon={AI_ICONS.copied}
              label="Sent"
              title="This reply has been posted in Slack"
              disabled
            />
          )
        ) : (
          <InlineSendConfirm
            hint="Post this reply in Slack?"
            disabled={!canSend || sending}
            loading={sending}
            onConfirm={() => void handleSend()}
          >
            <AiActionButton
              iconOnly={iconOnly}
              className="ai-draft-expanded__send-btn"
              icon={<SlackBrandIcon style={{ width: 14, height: 14, verticalAlign: "middle" }} />}
              label={sending ? "Sending…" : "Send to Slack"}
              title={canSend ? "Post this reply in the source Slack thread" : "Nothing to send yet"}
              disabled={!canSend || sending}
              loading={sending}
            />
          </InlineSendConfirm>
        )}

        {onRetry ? (
          <AiActionButton
            iconOnly={iconOnly}
            className="ai-draft-expanded__retry"
            icon={AI_ICONS.retry}
            label="Try again"
            title="Generate a fresh Slack reply for this action item"
            onClick={onRetry}
            loading={retryLoading}
            disabled={sending}
          />
        ) : null}
      </div>

      {hasMessage ? (
        sent ? (
          <pre className="ai-crm-expanded__note-body">{editedMessage}</pre>
        ) : (
          <div className="ai-crm-expanded__note-body-edit">
            <InlineEditableField
              value={editedMessage}
              onCommit={(next) => {
                setEditedMessage(next);
                return Promise.resolve();
              }}
              placeholder="Slack reply"
              ariaLabel="Slack reply message"
              multiline
            />
          </div>
        )
      ) : null}

    </div>
  );
};

/**
 * Expanded email/calendar AI Assist panel.
 *
 * `onResultPatch` must be called after Gmail send/edit so the parent can keep
 * those fields on the cached performer result. Local `draftOverride` is lost
 * when the sidebar remounts on a task switch.
 *
 * @example
 * <AIDraftExpanded result={emailResult} onResultPatch={(patch) => cache(patch)} />
 */
export const AIDraftExpanded = ({
  result,
  crmSideEffectResult = null,
  gmailAuthUrl,
  gmailPerformerReady,
  onGmailPerformerReady,
  onOpenGmailDraft,
  onRetry,
  onResultPatch,
  retryLoading,
  revisionNotes = "",
  iconOnly = false,
  gmailThreadId = "",
  sourceSnippet = "",
  calendarPrefillUrl = null,
  serverCalendarEventUrl = null,
}) => {
  const oauthPollStopRef = useRef(null);
  const [openingGmailDraft, setOpeningGmailDraft] = useState(false);
  const [calendarConnectLoading, setCalendarConnectLoading] = useState(false);
  const [showCalendarConnectNudge, setShowCalendarConnectNudge] = useState(false);
  const isCalendarTask = result?.type === "calendar";

  useEffect(() => {
    return () => {
      oauthPollStopRef.current?.();
    };
  }, []);

  useEffect(() => {
    if (!result?.draft || !isCalendarTask) {
      setShowCalendarConnectNudge(false);
      return;
    }
    setShowCalendarConnectNudge(shouldShowCalendarConnectNudge(result));
  }, [isCalendarTask, result]);

  const [pickedRecipient, setPickedRecipient] = useState("");
  const [manualRecipient, setManualRecipient] = useState("");
  const [draftOverride, setDraftOverride] = useState(null);
  // Live text from the subject/body editors, kept in sync on every keystroke
  // (see InlineEditableField's onDraftChange) so "Try again" can pick up an
  // edit that's still in progress -- e.g. clicking the retry button right
  // after typing, before the field's own blur-commit has landed.
  const [pendingSubjectDraft, setPendingSubjectDraft] = useState(null);
  const [pendingBodyDraft, setPendingBodyDraft] = useState(null);
  // Distinct from draftOverride, which also gets set by non-edit patches
  // (recipient change, Gmail send) -- these track only genuine subject/body
  // edits, so "Try again" only sends previousDraft/previousSubject (and the
  // backend's diff-and-apply-my-edits prompt framing) when something was
  // actually hand-edited, not on every plain retry.
  const [bodyWasEdited, setBodyWasEdited] = useState(false);
  const [subjectWasEdited, setSubjectWasEdited] = useState(false);
  const [updatingRecipient, setUpdatingRecipient] = useState(false);
  const [recipientError, setRecipientError] = useState("");
  const [sendingGmailDraft, setSendingGmailDraft] = useState(false);
  const [sentGmailDraft, setSentGmailDraft] = useState(() => Boolean(result?.gmail_sent_at));
  const [gmailSentAt, setGmailSentAt] = useState(() => String(result?.gmail_sent_at || "").trim());
  const [sendError, setSendError] = useState("");

  useEffect(() => {
    setDraftOverride(null);
    setPickedRecipient("");
    setManualRecipient("");
    setRecipientError("");
    setSendingGmailDraft(false);
    setSentGmailDraft(Boolean(result?.gmail_sent_at));
    setGmailSentAt(String(result?.gmail_sent_at || "").trim());
    setSendError("");
    setPendingSubjectDraft(null);
    setPendingBodyDraft(null);
    setBodyWasEdited(false);
    setSubjectWasEdited(false);
    // gmail_sent_at deliberately excluded: this effect resets editing state when the
    // draft's identity changes, not on every status flip (handleSendGmailDraft already
    // flips sentGmailDraft locally right after a successful send).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result?.gmail_draft_id, result?.client_item_id, result?.recipient_ambiguous]);

  const resolvedRecipientEmail = String(
    (draftOverride
      ? { ...result, ...draftOverride }
      : result
    )?.resolved_recipient_email || ""
  ).trim();

  // Unified message/CRM/Slack tasks render dedicated confirm panels (all hooks
  // above run first so the hook order stays stable regardless of result type).
  if (result?.type === "message") {
    return (
      <AIMessageExpanded
        result={result}
        onRetry={onRetry}
        retryLoading={retryLoading}
        onResultPatch={onResultPatch}
        iconOnly={iconOnly}
      />
    );
  }

  if (result?.type === "crm") {
    return (
      <AICrmExpanded
        result={result}
        onRetry={onRetry}
        retryLoading={retryLoading}
        iconOnly={iconOnly}
      />
    );
  }

  if (result?.type === "slack") {
    return (
      <AISlackExpanded
        result={result}
        onRetry={onRetry}
        retryLoading={retryLoading}
        iconOnly={iconOnly}
      />
    );
  }

  if (!result?.draft) return null;

  const displayResult = draftOverride ? { ...result, ...draftOverride } : result;
  const calendarEventTitle = isCalendarTask ? deriveCalendarEventTitle(displayResult.draft) : "";
  const attachmentNames = resolveAttachmentDisplayNames(result);

  const handleReconnectGmail = () => {
    if (!gmailAuthUrl) return;
    oauthPollStopRef.current?.();
    oauthPollStopRef.current = startPerformerGmailOAuthFlow(gmailAuthUrl, () => {
      onGmailPerformerReady?.();
      if (onRetry && !retryLoading) {
        onRetry();
      }
    });
  };

  const handleConnectCalendar = async () => {
    if (calendarConnectLoading) return;
    setCalendarConnectLoading(true);
    try {
      const authResp = await fetch(
        `${config.BACKEND_URL}/externalauth/google_calendar/auth_url`,
        { method: "GET", credentials: "include" }
      );
      const authPayload = await authResp.json();
      const authUrl = authPayload.auth_url || "";
      if (!authUrl) {
        setCalendarConnectLoading(false);
        return;
      }
      oauthPollStopRef.current?.();
      oauthPollStopRef.current = startPerformerCalendarOAuthFlow(authUrl, () => {
        dismissCalendarConnectNudge();
        setShowCalendarConnectNudge(false);
        setCalendarConnectLoading(false);
        if (onRetry && !retryLoading) {
          onRetry();
        }
      });
    } catch (_err) {
      setCalendarConnectLoading(false);
    }
  };

  const handleDismissCalendarNudge = () => {
    dismissCalendarConnectNudge();
    setShowCalendarConnectNudge(false);
  };

  const showReconnectForDraft =
    !isCalendarTask &&
    !displayResult.gmail_draft_url &&
    gmailAuthUrl &&
    !gmailPerformerReady &&
    (!displayResult.gmail_draft_error || gmailDraftErrorLooksLikeAuth(displayResult.gmail_draft_error));

  const gmailDraftUrl = normalizeGmailDraftUrl(displayResult.gmail_draft_url);
  const showOpenGmailDraft = Boolean(gmailDraftUrl || displayResult.gmail_draft_url);
  const connectGmailDraftLabel = gmailDraftErrorLooksLikeAuth(displayResult.gmail_draft_error)
    ? "Reconnect Gmail and save draft"
    : "Connect Gmail and save draft";

  const status = resolveAiDraftStatus(displayResult, {
    isCalendarTask,
    gmailPerformerReady,
    gmailAuthUrl,
    showReconnectForDraft,
    gmailDraftError: displayResult.gmail_draft_error || "",
  });
  const showRunDetails = shouldShowRunDetails(displayResult);
  const draftWasEdited =
    bodyWasEdited || subjectWasEdited || pendingBodyDraft != null || pendingSubjectDraft != null;
  const trimmedRevisionNotes = String(revisionNotes || "").trim();

  const recipientItemKey = String(
    displayResult?.client_item_id || displayResult?.key || ""
  ).trim();
  const recipientCandidates = Array.isArray(displayResult?.recipient_candidates)
    ? displayResult.recipient_candidates
    : [];
  const showRecipientPicker =
    !isCalendarTask &&
    (displayResult?.recipient_ambiguous === true ||
      (displayResult?.recipient_ambiguous === false &&
        !resolvedRecipientEmail &&
        Array.isArray(displayResult?.recipient_candidates)));
  const selectedRecipient = String(pickedRecipient || manualRecipient || "").trim();

  const handleUpdateRecipient = async () => {
    if (updatingRecipient || !recipientItemKey || !selectedRecipient) return;
    setUpdatingRecipient(true);
    setRecipientError("");
    try {
      const updated = await updateEmailDraft(recipientItemKey, { toEmail: selectedRecipient });
      const patch = {
        resolved_recipient_email: updated?.resolved_recipient_email || selectedRecipient,
        recipient_ambiguous: false,
        gmail_draft_id: updated?.gmail_draft_id || displayResult.gmail_draft_id,
        gmail_draft_url: updated?.gmail_draft_url || displayResult.gmail_draft_url,
        gmail_draft_error: null,
        subject: updated?.subject ?? displayResult.subject,
        draft: updated?.draft ?? displayResult.draft,
      };
      setDraftOverride((prev) => ({ ...prev, ...patch }));
      onResultPatch?.(patch);
    } catch (err) {
      setRecipientError(usageOrErrorMessage(err, "Failed to update recipient"));
    } finally {
      setUpdatingRecipient(false);
    }
  };

  const commitSubjectEdit = async (nextSubject) => {
    const updated = await updateEmailDraft(recipientItemKey, { subject: nextSubject });
    const patch = {
      subject: updated?.subject ?? nextSubject,
      draft: updated?.draft ?? displayResult.draft,
      gmail_draft_id: updated?.gmail_draft_id || displayResult.gmail_draft_id,
      gmail_draft_url: updated?.gmail_draft_url || displayResult.gmail_draft_url,
      gmail_draft_error: null,
    };
    setDraftOverride((prev) => ({ ...prev, ...patch }));
    setSubjectWasEdited(true);
    onResultPatch?.(patch);
  };

  const commitBodyEdit = async (nextBody) => {
    const updated = await updateEmailDraft(recipientItemKey, { body: nextBody });
    const patch = {
      subject: updated?.subject ?? displayResult.subject,
      draft: updated?.draft ?? nextBody,
      gmail_draft_id: updated?.gmail_draft_id || displayResult.gmail_draft_id,
      gmail_draft_url: updated?.gmail_draft_url || displayResult.gmail_draft_url,
      gmail_draft_error: null,
    };
    setDraftOverride((prev) => ({ ...prev, ...patch }));
    setBodyWasEdited(true);
    onResultPatch?.(patch);
  };

  const handleOpenGmailDraft = async () => {
    const subject = String(displayResult.subject || "").trim();
    if (onOpenGmailDraft) {
      if (!subject || openingGmailDraft) return;
      setOpeningGmailDraft(true);
      try {
        await onOpenGmailDraft({
          subject,
          draftUrl: gmailDraftUrl,
          gmailThreadId: String(
            result?.gmail_thread_id || gmailThreadId || ""
          ).trim(),
          sourceSnippet: String(sourceSnippet || "").trim(),
        });
      } finally {
        setOpeningGmailDraft(false);
      }
      return;
    }
    if (gmailDraftUrl) {
      window.open(gmailDraftUrl, "_blank", "noopener,noreferrer");
    }
  };

  const canSendGmailDraft =
    showOpenGmailDraft &&
    Boolean(recipientItemKey) &&
    Boolean(resolvedRecipientEmail) &&
    !showRecipientPicker;

  const handleSendGmailDraft = async () => {
    if (sendingGmailDraft || sentGmailDraft || !canSendGmailDraft) return;
    setSendingGmailDraft(true);
    setSendError("");
    try {
      const sent = await sendEmailDraft(recipientItemKey);
      const sentAt = String(sent?.gmail_sent_at || "").trim() || new Date().toISOString();
      const patch = {
        gmail_sent_at: sentAt,
        gmail_sent_message_id: sent?.gmail_message_id || displayResult.gmail_sent_message_id,
      };
      setSentGmailDraft(true);
      setGmailSentAt(sentAt);
      setDraftOverride((prev) => ({ ...prev, ...patch }));
      onResultPatch?.(patch);
    } catch (err) {
      setSendError(usageOrErrorMessage(err, "Failed to send email"));
    } finally {
      setSendingGmailDraft(false);
    }
  };

  const renderSendGmailDraftControl = () => {
    if (!showOpenGmailDraft) return null;
    if (sentGmailDraft) {
      const sentWhen = formatImplementedAtLabel(gmailSentAt || displayResult.gmail_sent_at);
      return (
        <AiActionButton
          iconOnly={iconOnly}
          className="ai-draft-expanded__send-btn ai-draft-expanded__send-btn--sent"
          icon={AI_ICONS.copied}
          label="Sent"
          title={sentWhen ? `Sent · ${sentWhen}` : "This email has been sent"}
          disabled
        />
      );
    }
    return (
      <InlineSendConfirm
        hint={resolvedRecipientEmail ? `Send to ${resolvedRecipientEmail}?` : ""}
        disabled={!canSendGmailDraft || sendingGmailDraft}
        loading={sendingGmailDraft}
        onConfirm={() => void handleSendGmailDraft()}
      >
        <AiActionButton
          iconOnly={iconOnly}
          className="ai-draft-expanded__send-btn"
          icon={AI_ICONS.gmail}
          label={sendingGmailDraft ? "Sending…" : "Send"}
          title={
            canSendGmailDraft
              ? `Send this email to ${resolvedRecipientEmail}`
              : "Add a recipient above before sending"
          }
          disabled={!canSendGmailDraft || sendingGmailDraft}
          loading={sendingGmailDraft}
        />
      </InlineSendConfirm>
    );
  };

  const renderOpenGmailDraftControl = () => {
    if (!showOpenGmailDraft) return null;
    if (onOpenGmailDraft) {
      return (
        <AiActionButton
          iconOnly={iconOnly}
          className="ai-draft-expanded__gmail-btn ai-draft-expanded__status-primary"
          icon={AI_ICONS.gmail}
          label={openingGmailDraft ? "Opening in Gmail…" : "Open draft in Gmail"}
          title={
            openingGmailDraft
              ? "Opening draft in Gmail…"
              : String(displayResult.subject || "").trim()
                ? `Find draft in Gmail: ${displayResult.subject}`
                : "Subject required to locate draft in Gmail"
          }
          onClick={() => void handleOpenGmailDraft()}
          disabled={!String(displayResult.subject || "").trim()}
          loading={openingGmailDraft}
          aria-busy={openingGmailDraft}
        >
          {openingGmailDraft ? (
            <AILoadingSpinner size="sm" className="ai-draft-expanded__gmail-btn-spinner" />
          ) : null}
        </AiActionButton>
      );
    }
    return (
      <a
        href={gmailDraftUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`ai-draft-expanded__gmail-btn ai-draft-expanded__status-primary${
          iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
        }`}
        title={gmailDraftUrl || "Open draft in Gmail (new tab)"}
      >
        {iconOnly ? (
          <span className="ai-icon-action-btn__glyph" aria-hidden="true">
            {AI_ICONS.gmail}
          </span>
        ) : (
          <>
            <span className="ai-text-action-btn__icon" aria-hidden="true">
              {AI_ICONS.gmail}
            </span>
            <span className="ai-text-action-btn__label">Open draft in Gmail ↗</span>
          </>
        )}
      </a>
    );
  };

  const renderCalendarPrimaryAction = () => {
    const prefillUrl = String(
      result?.calendar_prefill_url || calendarPrefillUrl || ""
    ).trim();
    if (!prefillUrl) return null;
    return (
      <a
        href={prefillUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`ai-draft-expanded__calendar-btn ai-draft-expanded__status-primary${
          iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
        }`}
        title="Opens a pre-filled Google Calendar invite — review notes/time, then Save (guests notified only if you send)"
      >
        {iconOnly ? (
          <span className="ai-icon-action-btn__glyph" aria-hidden="true">
            <AICalendarIcon />
          </span>
        ) : (
          <>
            <span className="ai-text-action-btn__icon" aria-hidden="true">
              <AICalendarIcon />
            </span>
            <span className="ai-text-action-btn__label">Review invite in Calendar ↗</span>
          </>
        )}
      </a>
    );
  };

  const renderGmailPrimaryAction = () => {
    if (result.gmail_draft_url) {
      return (
        <div className="ai-draft-expanded__status-actions">
          {renderOpenGmailDraftControl()}
          {renderSendGmailDraftControl()}
        </div>
      );
    }
    if (showReconnectForDraft) {
      return (
        <AiActionButton
          iconOnly={iconOnly}
          className="ai-draft-expanded__reconnect-btn ai-draft-expanded__status-primary"
          icon={AI_ICONS.reconnect}
          label={connectGmailDraftLabel}
          title={
            result.gmail_draft_error
              ? "This error may be fixed by reconnecting Gmail"
              : "Connect Gmail in a new tab, then this draft will be saved automatically"
          }
          onClick={handleReconnectGmail}
        />
      );
    }
    return null;
  };

  const renderStatusRowPrimaryAction = () => {
    if (isCalendarTask) return renderCalendarPrimaryAction();
    return renderGmailPrimaryAction();
  };

  const hasFailedDraftPlaceholder =
    !isCalendarTask && isDraftGenerationFailedPlaceholder(result.draft);

  const copyText = isCalendarTask
    ? calendarEventTitle
    : displayResult.subject
      ? `Subject: ${displayResult.subject}\n\n${displayResult.draft}`
      : displayResult.draft;

  return (
    <div className="ai-draft-expanded">
      <AIDraftStatusStrip
        tone={sentGmailDraft ? "success" : status.tone}
        label={sentGmailDraft ? "Sent" : status.label}
        hint={sentGmailDraft ? undefined : status.hint}
        implementedAt={sentGmailDraft ? gmailSentAt || displayResult.gmail_sent_at : ""}
      />
      {sendError ? renderUsageOrError(sendError, "p", "ai-run-details__line") : null}
      {isCalendarTask && showCalendarConnectNudge ? (
        <div className="ai-draft-calendar-nudge" role="status">
          <p className="ai-draft-calendar-nudge__text">
            We suggested a time using your defaults. Connect Google Calendar for personal
            availability.
          </p>
          <div className="ai-draft-calendar-nudge__actions">
            <AiActionButton
              iconOnly={false}
              className="ai-draft-expanded__reconnect-btn"
              icon={<AICalendarIcon />}
              label={calendarConnectLoading ? "Connecting…" : "Connect Calendar"}
              title="Connect Google Calendar in a new tab"
              onClick={() => void handleConnectCalendar()}
              loading={calendarConnectLoading}
              disabled={calendarConnectLoading}
            />
            <button
              type="button"
              className="ai-draft-calendar-nudge__dismiss"
              onClick={handleDismissCalendarNudge}
            >
              Not now
            </button>
          </div>
        </div>
      ) : null}
      <div className="ai-draft-expanded__actions ai-draft-expanded__toolbar">
        {hasFailedDraftPlaceholder ? null : (
          <CopyDraftButton iconOnly={iconOnly} text={copyText} />
        )}
        {renderStatusRowPrimaryAction()}
        {!isCalendarTask && serverCalendarEventUrl ? (
          <a
            href={serverCalendarEventUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`ai-draft-expanded__calendar-btn${
              iconOnly ? " ai-icon-action-btn" : " ai-text-action-btn"
            }`}
            title="Open the time block created in your Google Calendar"
          >
            {iconOnly ? (
              <span className="ai-icon-action-btn__glyph" aria-hidden="true">
                <AICalendarIcon />
              </span>
            ) : (
              <>
                <span className="ai-text-action-btn__icon" aria-hidden="true">
                  <AICalendarIcon />
                </span>
                <span className="ai-text-action-btn__label">Open in Google Calendar ↗</span>
              </>
            )}
          </a>
        ) : null}
        {onRetry ? (
          <AiActionButton
            iconOnly={iconOnly}
            className="ai-draft-expanded__retry"
            icon={AI_ICONS.retry}
            label="Try again"
            title={
              isCalendarTask
                ? "Generate a fresh calendar event for this action item"
                : trimmedRevisionNotes || draftWasEdited
                ? "Regenerate — applies your Note and/or draft edits as revision instructions"
                : "Generate a fresh draft for this action item"
            }
            onClick={() =>
              onRetry({
                revisionNotes: trimmedRevisionNotes,
                // Only carried along when something was actually edited --
                // displayResult.draft/subject are populated on every retry
                // (they're just "the current draft"), so sending them
                // unconditionally would make the backend apply "preserve
                // everything, diff for changes" framing to plain retries too.
                // Prefer whatever's live in the editors over the last-saved
                // value: a click on this button blurs an open editor just
                // before this handler runs, and the resulting commit save is
                // async, so `displayResult` may not have caught up yet.
                previousDraft: draftWasEdited
                  ? String(
                      pendingBodyDraft != null ? pendingBodyDraft : displayResult.draft || ""
                    )
                  : "",
                previousSubject: draftWasEdited
                  ? String(
                      pendingSubjectDraft != null
                        ? pendingSubjectDraft
                        : displayResult.subject || ""
                    )
                  : "",
              })
            }
            loading={retryLoading}
          />
        ) : null}
      </div>
      {!isCalendarTask && !hasFailedDraftPlaceholder ? (
        <p className="ai-draft-expanded__subject">
          <span className="ai-draft-expanded__subject-label">Subject</span>
          <InlineEditableField
            value={displayResult.subject || ""}
            onCommit={commitSubjectEdit}
            onDraftChange={setPendingSubjectDraft}
            placeholder="Add a subject"
            ariaLabel="Email subject"
            className="ai-inline-edit--subject"
          />
        </p>
      ) : null}
      {!isCalendarTask && resolvedRecipientEmail && !showRecipientPicker ? (
        <p className="ai-draft-expanded__subject">
          <span className="ai-draft-expanded__subject-label">To</span>
          <span className="ai-draft-expanded__subject-text">{resolvedRecipientEmail}</span>
        </p>
      ) : null}
      {showRecipientPicker ? (
        <div
          className="ai-crm-expanded__candidates"
          role="radiogroup"
          aria-label="Possible email recipients"
        >
          <p className="ai-crm-expanded__candidates-label">
            {displayResult?.recipient_ambiguous
              ? "Which address should this go to?"
              : "Add a recipient"}
          </p>
          {recipientCandidates.map((candidate) => (
            <label
              className="ai-crm-expanded__candidate-option"
              key={candidate.email}
            >
              <input
                type="radio"
                name={`email-recipient-${recipientItemKey || "draft"}`}
                checked={pickedRecipient === candidate.email && !manualRecipient}
                onChange={() => {
                  setPickedRecipient(candidate.email);
                  setManualRecipient("");
                }}
              />
              {candidate.label || candidate.email}
            </label>
          ))}
          <label className="ai-crm-expanded__candidate-option">
            <span>Other</span>
            <input
              type="email"
              className="ai-draft-expanded__recipient-input"
              placeholder="type an email address"
              value={manualRecipient}
              onChange={(event) => {
                setManualRecipient(event.target.value);
                setPickedRecipient("");
              }}
              aria-label="Type a recipient email"
            />
          </label>
          <button
            type="button"
            className="ai-text-action-btn ai-draft-expanded__recipient-update"
            onClick={() => void handleUpdateRecipient()}
            disabled={updatingRecipient || !selectedRecipient || !recipientItemKey}
          >
            {updatingRecipient ? "Updating…" : "Update recipient"}
          </button>
          {recipientError
            ? renderUsageOrError(recipientError, "p", "ai-run-details__line")
            : null}
        </div>
      ) : null}
      {isCalendarTask ? (
        <p className="ai-draft-expanded__calendar-event-title">
          <span className="ai-draft-expanded__subject-label">Event</span>
          <span className="ai-draft-expanded__subject-text">{calendarEventTitle}</span>
        </p>
      ) : hasFailedDraftPlaceholder ? (
        <p className="ai-draft-expanded__text ai-draft-expanded__text--error">
          The AI couldn&apos;t generate a draft for this item — use Try again to retry.
        </p>
      ) : (
        <div className="ai-draft-expanded__text">
          <InlineEditableField
            value={displayResult.draft || ""}
            onCommit={commitBodyEdit}
            onDraftChange={setPendingBodyDraft}
            placeholder="Email body"
            ariaLabel="Email body"
            multiline
          />
        </div>
      )}
      {!isCalendarTask && attachmentNames.length > 0 ? (
        <p className="ai-draft-expanded__attachments">
          <span className="ai-draft-expanded__attachments-label">Attachments</span>{" "}
          {attachmentNames.join(", ")}
        </p>
      ) : null}
      {showRunDetails ? (
        <AIRunDetails result={result} />
      ) : null}
      {!hasFailedDraftPlaceholder && onRetry && (trimmedRevisionNotes || draftWasEdited) ? (
        <p className="ai-draft-expanded__revision-hint">
          {trimmedRevisionNotes && draftWasEdited
            ? "Try again will apply your Note and your edits to this draft."
            : trimmedRevisionNotes
            ? "Try again will apply your Note as revision instructions."
            : "Try again will apply your edits to this draft."}
        </p>
      ) : null}
      {crmSideEffectResult ? (
        <div className="ai-draft-expanded__crm-side-effect">
          <p className="ai-draft-expanded__crm-side-effect-label">
            Also detected — CRM entry ready to review
          </p>
          <AICrmExpanded result={crmSideEffectResult} iconOnly={iconOnly} />
        </div>
      ) : null}
    </div>
  );
};
